import { useState } from "react";
import { Plus, Trash2, FileDown, Loader2, ScrollText, FileText, Lock, Briefcase, Megaphone, Camera } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { generateContract } from "../lib/api";
import { T } from "@/locales/tr";

const CONTRACT_KINDS = [
  { id: "revenue_share", label: "Partage de revenus", icon: ScrollText, defaultTitle: T("Contrat de partage équitable des revenus") },
  { id: "nda", label: "NDA · Confidentialité", icon: Lock, defaultTitle: T("Accord de confidentialité (NDA)") },
  { id: "service", label: "Prestation de service", icon: Briefcase, defaultTitle: T("Contrat de prestation de service") },
  { id: "sponsorship", label: "Sponsoring / Influence", icon: Megaphone, defaultTitle: T("Contrat de partenariat & sponsoring") },
  { id: "image_rights", label: "Cession droits image", icon: Camera, defaultTitle: T("Contrat de cession des droits à l'image") },
];

const emptyParty = (role = "") => ({ name: "", role, share_percent: 0 });

const defaultParties = (kind) => {
  switch (kind) {
    case "revenue_share": return [{ name: "", role: "Initiateur", share_percent: 60 }, { name: "", role: "Partenaire", share_percent: 40 }];
    case "nda": return [{ name: "", role: "Partie divulgatrice", share_percent: 0 }, { name: "", role: "Partie réceptrice", share_percent: 0 }];
    case "service": return [{ name: "", role: "Prestataire", share_percent: 0 }, { name: "", role: "Client", share_percent: 0 }];
    case "sponsorship": return [{ name: "", role: "Sponsor", share_percent: 0 }, { name: "", role: "Créateur / Influenceur", share_percent: 0 }];
    case "image_rights": return [{ name: "", role: "Modèle", share_percent: 0 }, { name: "", role: "Cessionnaire", share_percent: 0 }];
    default: return [emptyParty(), emptyParty()];
  }
};

export default function Contracts() {
  const [kind, setKind] = useState("revenue_share");
  const [title, setTitle] = useState(CONTRACT_KINDS[0].defaultTitle);
  const [purpose, setPurpose] = useState("");
  const [duration, setDuration] = useState("12 mois renouvelables");
  const [place, setPlace] = useState("Dakar, Sénégal");
  const [parties, setParties] = useState(defaultParties("revenue_share"));
  const [amountEur, setAmountEur] = useState("");
  const [deliverables, setDeliverables] = useState("");
  const [confidentialScope, setConfidentialScope] = useState("");
  const [imageUsage, setImageUsage] = useState("");
  const [imageTerritory, setImageTerritory] = useState("France et Sénégal");
  const [imageDuration, setImageDuration] = useState("5 ans");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const isRevenueShare = kind === "revenue_share";
  const showAmount = kind === "service" || kind === "sponsorship" || kind === "image_rights";
  const showDeliverables = kind === "service" || kind === "sponsorship";

  const total = parties.reduce((s, p) => s + Number(p.share_percent || 0), 0);

  const switchKind = (newKind) => {
    setKind(newKind);
    setParties(defaultParties(newKind));
    const k = CONTRACT_KINDS.find((c) => c.id === newKind);
    if (k) setTitle(k.defaultTitle);
    setResult("");
  };

  const updateParty = (i, key, value) => {
    setParties((arr) => arr.map((p, idx) => idx === i ? { ...p, [key]: key === "share_percent" ? Number(value) : value } : p));
  };

  const submit = async () => {
    if (!purpose.trim()) { toast.error(T("Précise l'objet du contrat.")); return; }
    if (parties.some((p) => !p.name.trim())) { toast.error(T("Chaque partie doit avoir un nom.")); return; }
    if (isRevenueShare && Math.abs(total - 100) > 0.01) { toast.error(`La somme des parts doit faire 100% (actuel : ${total}%).`); return; }
    setLoading(true); setResult("");
    try {
      const payload = {
        kind, title, purpose, parties, duration, place,
        ...(amountEur && showAmount ? { amount_eur: parseFloat(amountEur) } : {}),
        ...(deliverables && showDeliverables ? { deliverables } : {}),
        ...(confidentialScope && kind === "nda" ? { confidential_scope: confidentialScope } : {}),
        ...(kind === "image_rights" ? {
          image_usage: imageUsage || undefined,
          image_territory: imageTerritory || undefined,
          image_duration: imageDuration || undefined,
        } : {}),
      };
      const data = await generateContract(payload);
      setResult(data.text);
      toast.success(T("Contrat rédigé."));
    } catch { toast.error(T("Impossible de générer le contrat.")); }
    finally { setLoading(false); }
  };

  const downloadPdf = () => {
    if (!result) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 56;
    const pageWidth = doc.internal.pageSize.getWidth();
    const usable = pageWidth - margin * 2;
    doc.setFont("times", "bold");
    doc.setFontSize(16);
    doc.text(title, margin, margin);
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    const body = doc.splitTextToSize(result, usable);
    doc.text(body, margin, margin + 28);
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text("Design by Papicoo 🌹", margin, doc.internal.pageSize.getHeight() - 20);
    }
    doc.save(`${title.replace(/\s+/g, "_")}.pdf`);
  };

  return (
    <div data-testid="page-contracts" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Justice — al-‘adl")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2">{T("Contrats par Papicoo.")}</h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl leading-relaxed">
        {T("5 modèles formels, adossés au Coran et au droit moderne. Choisis le type, remplis le strict nécessaire — le contrat sort prêt à signer.")}
      </p>

      {/* Type selector */}
      <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {CONTRACT_KINDS.map((k) => {
          const Active = k.icon;
          const selected = kind === k.id;
          return (
            <button
              key={k.id}
              data-testid={`kind-${k.id}`}
              onClick={() => switchKind(k.id)}
              className={`p-3 rounded-xl border text-left transition-all ${selected
                ? "bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] border-[color:var(--papicoo-ink)]"
                : "bg-white border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/60"}`}
            >
              <Active size={18} className={selected ? "text-[color:var(--papicoo-gold)]" : "text-[color:var(--papicoo-accent)]"} />
              <div className={`text-xs font-medium mt-2 leading-tight ${selected ? "text-[color:var(--papicoo-sand)]" : "text-[color:var(--papicoo-ink)]"}`}>
                {T(k.label)}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid lg:grid-cols-[1fr_1.2fr] gap-4 lg:gap-6">
        <div className="papicoo-card p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Titre")}</label>
            <input data-testid="contract-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Objet")}</label>
            <textarea data-testid="contract-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30" placeholder={kind === "nda" ? T("Ex : Mission de conseil sur le lancement d'une marque") : kind === "image_rights" ? T("Ex : Séance photo portrait du 12 mars 2026") : T("Ex : Création d'une marque de vêtements, production de contenu...")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Durée")}</label>
              <input data-testid="contract-duration" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Lieu")}</label>
              <input data-testid="contract-place" value={place} onChange={(e) => setPlace(e.target.value)} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
            </div>
          </div>

          {/* Champs dynamiques par kind */}
          {showAmount && (
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Montant (€)")}</label>
              <input data-testid="contract-amount" type="number" step="0.01" value={amountEur} onChange={(e) => setAmountEur(e.target.value)} placeholder={kind === "image_rights" ? T("0 si à titre gracieux") : "Ex : 1500"} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
            </div>
          )}
          {showDeliverables && (
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{kind === "sponsorship" ? "Prestations attendues" : "Livrables"}</label>
              <textarea data-testid="contract-deliverables" rows={2} value={deliverables} onChange={(e) => setDeliverables(e.target.value)} placeholder={kind === "sponsorship" ? T("Ex : 3 posts Instagram, 2 stories par semaine, 1 vidéo TikTok") : T("Ex : Identité visuelle complète, brand book, 10 visuels réseaux")} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
            </div>
          )}
          {kind === "nda" && (
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Périmètre confidentiel")}</label>
              <textarea data-testid="contract-confidential-scope" rows={2} value={confidentialScope} onChange={(e) => setConfidentialScope(e.target.value)} placeholder={T("Ex : code source, listes clients, données financières, savoir-faire métier")} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
            </div>
          )}
          {kind === "image_rights" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Usage prévu")}</label>
                <input data-testid="contract-image-usage" value={imageUsage} onChange={(e) => setImageUsage(e.target.value)} placeholder={T("Ex : Web, réseaux sociaux, affichage publicitaire")} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Territoire")}</label>
                <input data-testid="contract-image-territory" value={imageTerritory} onChange={(e) => setImageTerritory(e.target.value)} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Durée d'exploitation")}</label>
                <input data-testid="contract-image-duration" value={imageDuration} onChange={(e) => setImageDuration(e.target.value)} className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm" />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">Parties {isRevenueShare && "& parts"}</label>
              <button data-testid="add-party" onClick={() => setParties((a) => [...a, emptyParty()])} className="text-xs inline-flex items-center gap-1 text-[color:var(--papicoo-ink)]">
                <Plus size={14} /> {T("Ajouter")}
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {parties.map((p, i) => (
                <div key={`party-${i}-${p.role || "x"}`} className={`grid ${isRevenueShare ? "grid-cols-12" : "grid-cols-11"} gap-2 items-center`}>
                  <input data-testid={`party-name-${i}`} placeholder={T("Nom")} value={p.name} onChange={(e) => updateParty(i, "name", e.target.value)} className={`${isRevenueShare ? "col-span-5" : "col-span-5"} bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm`} />
                  <input data-testid={`party-role-${i}`} placeholder={T("Rôle")} value={p.role} onChange={(e) => updateParty(i, "role", e.target.value)} className={`${isRevenueShare ? "col-span-4" : "col-span-5"} bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm`} />
                  {isRevenueShare && (
                    <input data-testid={`party-share-${i}`} type="number" min="0" max="100" placeholder="%" value={p.share_percent} onChange={(e) => updateParty(i, "share_percent", e.target.value)} className="col-span-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  )}
                  <button data-testid={`remove-party-${i}`} onClick={() => setParties((a) => a.filter((_, idx) => idx !== i))} className="col-span-1 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-accent)]">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            {isRevenueShare && (
              <div className={`mt-2 text-xs ${Math.abs(total - 100) < 0.01 ? "text-emerald-700" : "text-[color:var(--papicoo-accent)]"}`}>
                {T("Total des parts :")} {total}%
              </div>
            )}
          </div>

          <button data-testid="generate-contract-btn" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} {T("Rédiger le contrat")}
          </button>
        </div>

        <div className="papicoo-card p-6 max-h-[80vh] overflow-y-auto" data-testid="contract-result">
          {result ? (
            <>
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <div className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Aperçu du contrat")}</div>
                <button data-testid="download-contract-pdf" onClick={downloadPdf} className="inline-flex items-center gap-2 text-sm papicoo-btn-gold px-4 py-2 rounded-full">
                  <FileDown size={14} /> {T("Télécharger en PDF")}
                </button>
              </div>
              <pre className="whitespace-pre-wrap break-words font-serif text-[15px] leading-relaxed text-[color:var(--papicoo-text)]">{result}</pre>
            </>
          ) : (
            <div className="text-sm italic text-[color:var(--papicoo-muted)]">
              {T("« Pratiquez l'équité : cela est plus proche de la piété. » — Al-Mâ'ida 5:8")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
